// src/pages/FaqPage.js
// Content lives in src/data/faq.json so scripts/seo/build.js can also publish it
// as /docs/faq.md for crawlers and AI answer engines that don't run JavaScript.
import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Mascot from '../components/Mascot';
import faq from '../data/faq.json';
import '../styles/pages/Page.css';
import '../styles/pages/LegalPage.css';
import '../styles/components/StatePage.css';

const TITLE = 'Michii FAQ: Interactive Step-by-Step Guides, Answered';
const DESCRIPTION = 'Answers about Michii, the Chrome extension that records any web workflow once and replays it as an interactive step-by-step guide: setup, playback, sharing, privacy and pricing.';
const URL = 'https://michii.app/faq';

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

const JSON_LD = JSON.stringify({
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'FAQPage',
      '@id': `${URL}#faq`,
      url: URL,
      name: TITLE,
      description: DESCRIPTION,
      mainEntity: faq.flatMap((s) => s.items).map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    },
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://michii.app/' },
        { '@type': 'ListItem', position: 2, name: 'FAQ', item: URL },
      ],
    },
  ],
});

// Swap the SPA's default head tags for this page, then put them back.
function useFaqHead() {
  useEffect(() => {
    const desc = document.querySelector('meta[name="description"]');
    const canon = document.querySelector('link[rel="canonical"]');
    const prev = { title: document.title, desc: desc?.content, canon: canon?.href };

    document.title = TITLE;
    if (desc) desc.content = DESCRIPTION;
    if (canon) canon.href = URL;
    const ld = document.createElement('script');
    ld.type = 'application/ld+json';
    ld.text = JSON_LD;
    document.head.appendChild(ld);

    return () => {
      document.title = prev.title;
      if (desc) desc.content = prev.desc;
      if (canon) canon.href = prev.canon;
      ld.remove();
    };
  }, []);
}

export default function FaqPage() {
  useFaqHead();

  return (
    <div className="page-container">
      <div className="container legal-container">
        <header className="legal-header">
          <p className="m-eyebrow">Help · FAQ</p>
          <h1 className="page-title">Questions, answered.</h1>
          <p className="faq-lede">
            How Michii records a workflow once and plays it back as a step-by-step guide on the live page.
          </p>
        </header>

        <nav className="faq-toc" aria-label="FAQ sections">
          {faq.map((s) => (
            <a key={s.section} href={`#${slug(s.section)}`}>{s.section}</a>
          ))}
        </nav>

        <div className="legal-body">
          {faq.map((s) => (
            <section key={s.section} id={slug(s.section)} className="legal-section">
              <h2>{s.section}</h2>
              <div className="faq-list">
                {s.items.map((f) => (
                  // Answers stay in the DOM when closed, so they're still indexable.
                  <details key={f.q} className="faq-item">
                    <summary>{f.q}</summary>
                    <p>{f.a}</p>
                  </details>
                ))}
              </div>
            </section>
          ))}
        </div>

        <div className="faq-end">
          <div className="state-say">
            <Mascot size={40} />
            <div className="state-say__bubble">
              <b>Michii says</b>
              <span>Didn’t find your question? Ask us directly.</span>
            </div>
          </div>
          <Link to="/contact" className="lp-btn">Contact us</Link>
        </div>
      </div>
    </div>
  );
}
